import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import axiosConnector, { axiosInstance } from "../api/axiosConnector";

interface User {
  id: number;
  name: string;
  username: string;
  firstname: string;
  lastname: string;
  avatarSrc: string;
  email: string;
  phone_number: string;
  role: string;
}

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface LoginResponse {
  user_id: number;
  access_token: string;
  firstname: string;
  lastname: string;
  profile_pic: string;
  email: string;
  phone_number: string;
  role: string;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setAuthToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem("authToken");
    return storedToken;
  });
  const [user, setUser] = useState<User | null>(null);

  const fetchUserData = async (username: string) => {
    try {
      const response = await axiosConnector.get<LoginResponse>("/auth/me");
      const { firstname, lastname, profile_pic, email, phone_number, role } = response.data;
      const userData = { 
        id: response.data.user_id,
        name: username, 
        username,
        firstname: firstname,
        lastname: lastname,
        avatarSrc: profile_pic,
        email: email,
        phone_number: phone_number,
        role: role
      };
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      logout();
    }
  };

  useEffect(() => {
    if (token) {
      // When we have a token, fetch the user data
      const username = localStorage.getItem("username");
      if (username) {
        fetchUserData(username);
      }
    }
  }, [token]);

  const login = async (username: string, password: string) => {
    try {
      const response = await axiosConnector.post<LoginResponse>("/auth/login", {
        username,
        password,
      });
      const newToken = response.data.access_token;
      const { firstname, lastname, profile_pic, email, phone_number, role } = response.data;
      const userData = { 
        id: response.data.user_id,
        name: username, 
        username,
        firstname: firstname,
        lastname: lastname,
        avatarSrc: profile_pic,
        email: email,
        phone_number: phone_number,
        role: role
      };

      localStorage.setItem("authToken", newToken);
      localStorage.setItem("username", username);
      setAuthToken(newToken);
      setUser(userData);
      
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
    } catch (error) {
      console.error('Login error:', error);
      throw new Error("Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("username");
    setAuthToken(null);
    setUser(null);
  };

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, user, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
